/**
 * Where copied entities go: the system clipboard, so a copy reaches another
 * tab or level (Unreal copies actors as text for the same reason), with the
 * last copy kept in the editor for when the browser refuses clipboard access
 * (permission denied, an insecure context, or no clipboard API at all).
 *
 * It holds text only; what the text means is entityCopies' concern.
 */

/** The async clipboard API's text half (`navigator.clipboard`). */
export interface SystemClipboard {
  writeText(text: string): Promise<void>;
  readText(): Promise<string>;
}

export class EntityClipboard {
  private last: string | null = null;

  constructor(private readonly system: SystemClipboard | null) {}

  /** Keeps text a copy event already put on the system clipboard, as the fallback. */
  hold(text: string): void {
    this.last = text;
  }

  /** The last copy made in this editor, or null. */
  kept(): string | null {
    return this.last;
  }

  async write(text: string): Promise<void> {
    this.last = text;
    try {
      await this.system?.writeText(text);
    } catch {
      // Refused: the kept copy still pastes in this tab.
    }
  }

  /**
   * The system clipboard's text, or the last copy here when it can't be read
   * or holds no text at all (as clipboard events treat it).
   */
  async read(): Promise<string | null> {
    if (this.system) {
      try {
        const text = await this.system.readText();
        if (text !== "") return text;
      } catch {
        // Refused (a denied read permission): fall back to the kept copy.
      }
    }
    return this.last;
  }
}
